import type {
  ChartSection,
  ChartElement,
  ElementsBySection,
  LexicalRoot,
  ChartSectionNode,
  ChartElementNode,
  ChartChanges,
} from '@/types';

/**
 * Convert ChartSection and ChartElement API data to Lexical JSON format
 * @param sections - Array of chart sections from API
 * @param elementsBySection - Object mapping section_id to array of elements
 * @returns Lexical editor state JSON
 */
export function chartSectionsToLexical(
  sections: ChartSection[] | null | undefined,
  elementsBySection: ElementsBySection,
): LexicalRoot {
  const root: LexicalRoot = {
    root: {
      children: [],
      direction: 'ltr',
      format: '',
      indent: 0,
      type: 'root',
      version: 1,
    },
  };

  if (!sections || sections.length === 0) {
    return root;
  }

  // Sort sections by order
  const sortedSections = [...sections].sort(
    (a, b) => (a.order || 0) - (b.order || 0),
  );

  sortedSections.forEach((section) => {
    const sectionNode: ChartSectionNode = {
      children: [],
      direction: 'ltr',
      format: '',
      indent: 0,
      type: 'chart-section',
      version: 1,
      heading: section.heading || '',
      style: section.style || 'h1',
      number: section.number || null,
      entity_id: section.entity_id || null,
    };

    // Get elements for this section, sorted by order
    const sectionId = section.entity_id || '';
    const elements = (elementsBySection[sectionId] || []).sort(
      (a, b) => (a.order || 0) - (b.order || 0),
    );

    elements.forEach((element) => {
      const elementNode: ChartElementNode = {
        children: [
          {
            children: [
              {
                detail: 0,
                format: 0,
                mode: 'normal',
                style: '',
                text: element.description || '',
                type: 'text',
                version: 1,
              },
            ],
            direction: 'ltr',
            format: '',
            indent: 0,
            type: 'paragraph',
            version: 1,
          },
          {
            children: [
              {
                detail: 0,
                format: 0,
                mode: 'normal',
                style: '',
                text: element.prompt || '',
                type: 'text',
                version: 1,
              },
            ],
            direction: 'ltr',
            format: '',
            indent: 0,
            type: 'paragraph',
            version: 1,
          },
        ],
        direction: 'ltr',
        format: '',
        indent: 0,
        type: 'chart-element',
        version: 1,
        description: element.description || '',
        prompt: element.prompt || '',
        element_type: element.type || 'narrative',
        for_each: element.for_each || false,
        waypoints: element.waypoints || [],
        metadata: element.metadata || null,
        entity_id: element.entity_id || null,
      };
      sectionNode.children.push(elementNode);
    });

    root.root.children.push(sectionNode);
  });

  return root;
}

/**
 * Convert Lexical JSON back to ChartSection and ChartElement arrays
 * @param lexicalJson - Lexical editor state JSON
 * @returns Object with sections and elements arrays
 */
export function lexicalToChartSections(lexicalJson: LexicalRoot | null | undefined): {
  sections: ChartSection[];
  elements: ChartElement[];
} {
  const sections: ChartSection[] = [];
  const elements: ChartElement[] = [];

  if (!lexicalJson || !lexicalJson.root || !lexicalJson.root.children) {
    return { sections, elements };
  }

  lexicalJson.root.children.forEach((sectionNode, sectionIndex) => {
    if (sectionNode.type !== 'chart-section') {
      return;
    }

    const section: ChartSection = {
      heading: sectionNode.heading || '',
      style: sectionNode.style || 'h1',
      number: sectionNode.number || null,
      order: sectionIndex,
    };

    if (sectionNode.entity_id) {
      section.entity_id = sectionNode.entity_id;
    }

    sections.push(section);

    // Extract elements from section children
    if (sectionNode.children) {
      sectionNode.children.forEach((elementNode, elementIndex) => {
        if (elementNode.type !== 'chart-element') {
          return;
        }

        // Extract description and prompt from paragraph children
        let description = elementNode.description || '';
        let prompt = elementNode.prompt || '';

        if (elementNode.children && elementNode.children.length >= 2) {
          const descPara = elementNode.children[0];
          const promptPara = elementNode.children[1];
          if (descPara && descPara.children && descPara.children[0]) {
            description = descPara.children[0].text || description;
          }
          if (promptPara && promptPara.children && promptPara.children[0]) {
            prompt = promptPara.children[0].text || prompt;
          }
        }

        const element: ChartElement = {
          description,
          prompt,
          type: elementNode.element_type || 'narrative',
          for_each: elementNode.for_each || false,
          waypoints: elementNode.waypoints || [],
          metadata: elementNode.metadata || null,
          order: elementIndex,
          section_id: section.entity_id || null,
        };

        if (elementNode.entity_id) {
          element.entity_id = elementNode.entity_id;
        }

        elements.push(element);
      });
    }
  });

  return { sections, elements };
}

/**
 * Calculate changes between original and new sections/elements
 * @param originalSections - Original sections from API
 * @param originalElements - Original elements from API
 * @param newSections - New sections from Lexical
 * @param newElements - New elements from Lexical
 * @returns Object with sectionsToCreate, sectionsToUpdate, sectionsToDelete, elementsToCreate, elementsToUpdate, elementsToDelete
 */
export function calculateChanges(
  originalSections: ChartSection[] | null | undefined,
  originalElements: ChartElement[] | null | undefined,
  newSections: ChartSection[] | null | undefined,
  newElements: ChartElement[] | null | undefined,
): ChartChanges {
  const sectionsToCreate: ChartSection[] = [];
  const sectionsToUpdate: ChartSection[] = [];
  const sectionsToDelete: ChartSection[] = [];
  const elementsToCreate: ChartElement[] = [];
  const elementsToUpdate: ChartElement[] = [];
  const elementsToDelete: ChartElement[] = [];

  // Create maps for easier lookup
  const originalSectionMap = new Map<string, ChartSection>();
  (originalSections || []).forEach((section) => {
    if (section.entity_id) {
      originalSectionMap.set(section.entity_id, section);
    }
  });

  const newSectionMap = new Map<string, ChartSection>();
  (newSections || []).forEach((section) => {
    if (section.entity_id) {
      newSectionMap.set(section.entity_id, section);
    }
  });

  const originalElementMap = new Map<string, ChartElement>();
  (originalElements || []).forEach((element) => {
    if (element.entity_id) {
      originalElementMap.set(element.entity_id, element);
    }
  });

  const newElementMap = new Map<string, ChartElement>();
  (newElements || []).forEach((element) => {
    if (element.entity_id) {
      newElementMap.set(element.entity_id, element);
    }
  });

  // Process sections
  (newSections || []).forEach((section) => {
    if (!section.entity_id) {
      // New section
      sectionsToCreate.push(section);
    } else {
      const original = originalSectionMap.get(section.entity_id);
      if (original) {
        // Check if section has changed
        if (
          original.heading !== section.heading ||
          original.style !== section.style ||
          original.number !== section.number ||
          original.order !== section.order
        ) {
          sectionsToUpdate.push(section);
        }
      }
    }
  });

  // Find deleted sections
  (originalSections || []).forEach((section) => {
    if (section.entity_id && !newSectionMap.has(section.entity_id)) {
      sectionsToDelete.push(section);
    }
  });

  // Process elements
  (newElements || []).forEach((element) => {
    if (!element.entity_id) {
      // New element
      elementsToCreate.push(element);
    } else {
      const original = originalElementMap.get(element.entity_id);
      if (original) {
        // Check if element has changed
        if (
          original.description !== element.description ||
          original.prompt !== element.prompt ||
          original.type !== element.type ||
          original.for_each !== element.for_each ||
          JSON.stringify(original.waypoints || []) !==
            JSON.stringify(element.waypoints || []) ||
          JSON.stringify(original.metadata || null) !==
            JSON.stringify(element.metadata || null) ||
          original.order !== element.order ||
          original.section_id !== element.section_id
        ) {
          elementsToUpdate.push(element);
        }
      }
    }
  });

  // Find deleted elements
  (originalElements || []).forEach((element) => {
    if (element.entity_id && !newElementMap.has(element.entity_id)) {
      elementsToDelete.push(element);
    }
  });

  return {
    sectionsToCreate,
    sectionsToUpdate,
    sectionsToDelete,
    elementsToCreate,
    elementsToUpdate,
    elementsToDelete,
  };
}

